import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';

import { ErrorService, MultilingualValidators } from '@portal/shared/ui/form';
import { ActionAmlTimeouts } from './action-aml-timeout.list';
import { OpeningHoursDays } from './opening-hours-day.list';
import { IBusinessInformation } from '../interfaces/business-information.interface';
import { ActionAmlTimeout } from '../enums/action-aml-timeout.enum';
import { IOpeningHours } from '../interfaces/opening-hours.interface';
import { IOpeningHoursTime } from '../interfaces/opening-hours-times.interface';
import { OpeningHoursDay } from '../enums/opening-hours-day.enum';

declare const $localize;

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'portal-form-business-information',
    templateUrl: './form-business-information.component.html',
    standalone: false
})
export class FormBusinessInformationComponent implements OnChanges {
  @Input() form: UntypedFormGroup;
  @Input() value: IBusinessInformation;
  @Input() disabled: boolean;

  formControls: { [key: string]: [string | string[], ValidatorFn[]] };

  isInvalid = ErrorService.isFieldInvalid;

  constructor(
    public actionAmlTimeouts: ActionAmlTimeouts,
    public openingHoursDays: OpeningHoursDays,
    private customValidators: MultilingualValidators,
  ) {
    this.formControls = {
      actionAMLTimeout: [ActionAmlTimeout.Allow, []],
      openingHours: [[], [this.customValidators.markOneOption]],
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (get(changes, 'form.currentValue') && get(changes, 'form.firstChange')) {
      forEach(this.formControls, (formControl: [string, ValidatorFn[]], key: string) => {
        const [state, validators]: [string, ValidatorFn[]] = formControl;
        this.form.setControl(key, new UntypedFormControl(state, validators));
      });
    }

    if (get(changes, 'value.currentValue')) {
      this.form.patchValue(this.value);
      this.buildOpeningHoursForm();

      if (this.disabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  get actionAmlTimeout(): AbstractControl {
    return this.form.get('actionAMLTimeout');
  }

  get openingHours(): AbstractControl {
    return this.form.get('openingHours');
  }

  get openingHoursForm(): UntypedFormArray {
    return this.form.get('openingHoursForm') as UntypedFormArray;
  }

  onOpeningHoursChanged(control: UntypedFormGroup): void {
    const openingHours: IOpeningHours[] = [];
    this.openingHoursForm.value.forEach((value, i) => {
      value.times.forEach((timeslot: IOpeningHoursTime) => {
        if (timeslot.start && timeslot.end) {
          openingHours.push({
            day: this.openingHoursDays.list[i].id as OpeningHoursDay,
            times: timeslot,
          });
        }
      });
    });
    this.openingHours.setValue(openingHours);

    control.patchValue({
      timesString: this.getTimesString(control),
    });
  }

  addTimeslot(control: UntypedFormArray): void {
    control.push(this.createTimesForm());
  }

  removeTimeslot(control: UntypedFormArray, index: number): void {
    control.removeAt(index);
    this.onOpeningHoursChanged(control.parent as UntypedFormGroup);
  }

  private getTimesString(control?: UntypedFormGroup): string {
    const closed = $localize`Closed`;

    if (!control) {
      return closed;
    }

    if (control.invalid) {
      return control.value.timesString;
    }

    const timesString = control.value.times
      .map((timeslot: IOpeningHoursTime) => {
        if (timeslot.start && timeslot.end) {
          return `${timeslot.start} - ${timeslot.end}`;
        }
      })
      .filter((timeslot: string) => timeslot)
      .join(', ');

    return timesString || closed;
  }

  private createTimesForm(value?: IOpeningHoursTime): UntypedFormGroup {
    return new UntypedFormGroup(
      {
        start: new UntypedFormControl(get(value, 'start', ''), this.customValidators.time),
        end: new UntypedFormControl(get(value, 'end', ''), this.customValidators.time),
      },
      this.customValidators.timePair,
    );
  }

  private buildOpeningHoursForm(): void {
    const controls = this.openingHoursDays.list.map((day) => {
      const openDays = this.openingHours.value.filter(
        (value: IOpeningHours) => value.day === day.id,
      );

      if (openDays.length) {
        const control = new UntypedFormGroup({
          isEditing: new UntypedFormControl(false),
          times: new UntypedFormArray(
            openDays.map((openDay: IOpeningHours) => {
              return this.createTimesForm(openDay.times);
            }),
            [this.customValidators.timePairOverlap],
          ),
        });
        control.setControl('timesString', new UntypedFormControl(this.getTimesString(control)));
        return control;
      }

      return new UntypedFormGroup({
        isEditing: new UntypedFormControl(false),
        times: new UntypedFormArray([this.createTimesForm()], this.customValidators.timePairOverlap),
        timesString: new UntypedFormControl(this.getTimesString()),
      });
    });

    this.form.setControl('openingHoursForm', new UntypedFormArray(controls));
  }
}
